import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import {
  ICashUpListReport,
  IDailySalesReport,
  IDeposits,
  IReportDelivery,
  IReportSales,
  IReportTransfers,
  IStockMovementReport,
  ITaxReport,
  OrderDateCriteria,
  OrderUserCriteria,
} from '@boma/supplier-portal/shared/entities';
import { SalesRecordReportType } from 'libs/supplier-portal/reports/src/lib/containers/sales/_base/base-sales-record.component';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HelperFunctionsService } from '../helper-functions/helper-functions.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient,
    private helperService: HelperFunctionsService
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  private loadSalesRecordInternal(
    salesRecordType: SalesRecordReportType,
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    customerIds: string[] = [],
    userId: string = '',
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    handshake: boolean | null = null,
    deliveredByUserId: string = ''
  ) {
    const params = {
      locationIds: locationIds,
      definitionsFilters,
      productIds: productIds,
      productFilters: {
        brandIds: selectedBrands,
        categoryIds: selectedCategories,
        departmentIds: selectedDepartments,
      },
      customerEntityIds: customerIds,
      entityId,
      startDate,
      endDate,
      handshake,
      deliveredByUserId,
      userId,
    };
    if (salesRecordType === 'purchases') {
      return this.httpClient.post<IReportSales>(
        `${this.base}api/v${this.version}/${entityId}/Reports/PurchaseRecord`,
        this.helperService.sanitizeParams(params)
      );
    }

    return this.httpClient.post<IReportSales>(
      `${this.base}api/v${this.version}/${entityId}/Reports/SalesRecord`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadReportSales(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    customerIds: string[] = [],
    userId: string = '',
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    handshake: boolean | null = null,
    deliveredByUserId: string = ''
  ): Observable<IReportSales> {
    return this.loadSalesRecordInternal(
      'sales',
      entityId,
      locationIds,
      definitionsFilters,
      productIds,
      customerIds,
      userId,
      startDate,
      endDate,
      selectedCategories,
      selectedDepartments,
      selectedBrands,
      handshake,
      deliveredByUserId
    );
  }

  loadReportPurchases(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    customerIds: string[] = [],
    userId: string = '',
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    handshake: boolean | null = null,
    deliveredByUserId: string = ''
  ): Observable<IReportSales> {
    return this.loadSalesRecordInternal(
      'purchases',
      entityId,
      locationIds,
      definitionsFilters,
      productIds,
      customerIds,
      userId,
      startDate,
      endDate,
      selectedCategories,
      selectedDepartments,
      selectedBrands,
      handshake,
      deliveredByUserId
    );
  }

  /**
   * @param [purchases=false] - if true, loads a purchase report, otherwise a sales report
   */
  loadDailySalesSummary(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    customerIds: string[] = [],
    userId: string = '',
    orderTypes: string[] = [],
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    customerReport: boolean = false,
    handshake: boolean | null = null, // selected fulfilment
    saleOrigin: string[] = ['Customer', 'Manual', 'POS'], // customer manual pos TODO MAKE THIS BETTER ON API
    purchases: boolean = false
  ): Observable<IDailySalesReport> {
    const params = {
      customerReport: customerReport,
      locationIds: locationIds,
      definitionsFilters,
      productIds: productIds,
      customerIds: customerIds,
      entityId,
      startDate,
      endDate,
      productFilters: {
        brandIds: selectedBrands,
        categoryIds: selectedCategories,
        departmentIds: selectedDepartments,
      },
      userId: userId === '' ? null : userId,
      orderTypes: orderTypes,
      handshake,
      saleOrigin,
      purchases
    };

    return this.httpClient.post<IDailySalesReport>(
      `${this.base}api/v${this.version}/${entityId}/Reports/DailySales`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadStockSales(
    entityId: string, //required
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    page: string = '1',
    perPage: string = '500',
    categoryIds: string[] = [],
    brandIds: string[] = [],
    endDate: string = '', // will be implemented soon
    departmentIds: string[] = []
  ) {
    const params = {
      entityId: entityId,
      definitionsFilters,
      productFilters: {
        brandIds: brandIds,
        categoryIds: categoryIds,
        departmentIds: departmentIds,
      },
      productIds: productIds,
      locationIds: locationIds,
      endDate: endDate,
      perPage: Number(perPage),
      page: Number(page),
      locationType: null,
    };

    return this.httpClient.post<IReportSales>(
      `${this.base}api/v${this.version}/${entityId}/Stock/Summary/Report`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadCashUpReport(
    entityId: string,
    locationId: string,
    userId: string = '',
    startDate: string = '',
    endDate: string = ''
  ) {
    const params = {
      startDate: startDate,
      endDate: endDate,
    };

    if (userId) {
      params['userId'] = userId;
    }
    // TODO check if location can be skipped here or if all is valid if not remove.
    if (
      locationId &&
      locationId !== 'All Locations' &&
      locationId !== 'All Vehicles'
    ) {
      params['locationId'] = locationId;
    }

    this.helperService.sanitizeParams(params);
    return this.httpClient.get<IReportSales>(
      `${this.base}api/v${this.version}/${entityId}/Reports/CashUp`,
      {
        params,
      }
    );
  }

  loadCashUpList(params: {
    entityId: string;
    locationIds: string[];
    definitionsFilters: { targets: number; definitionIds: string[] };
    userId?: string;
    startDate: string;
    endDate: string;
  }): Observable<ICashUpListReport> {
    return this.httpClient.post<ICashUpListReport>(
      `${this.base}api/v${this.version}/${params.entityId}/Reports/CashUp/List`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadDepositReport(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    userId: string = '',
    eventType: string = '',
    startDate: string = '',
    endDate: string = ''
  ) {
    const params = {
      locationIds,
      definitionsFilters,
      entityId,
      startDate,
      endDate,
    };

    if (userId) {
      params['userId'] = userId;
    }

    if (eventType) {
      params['eventType'] = eventType;
    }

    return this.httpClient.post<IDeposits>(
      `${this.base}api/v${this.version}/${entityId}/Reports/Deposits`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadStockMovementReport(params: {
    entityId: string;
    locationIds: string[];
    definitionsFilters: { targets: number; definitionIds: string[] };
    productIds: string[];
    userId?: string;
    startDate: string;
    endDate: string;
    productFilters: {
      brandIds: string[];
      categoryIds: string[];
      departmentIds: string[];
    };
  }) {
    return this.httpClient
      .post<IStockMovementReport>(
        `${this.base}api/v${this.version}/${params.entityId}/Reports/Inventory/StockMovement`,
        this.helperService.sanitizeParams(params)
      )
      .pipe(catchError(() => of(null)));
  }

  // generateStockMovementReport(params: {
  //   entityId: string;
  //   locationIds: string[];
  //   definitionsFilters: {
  //     targets: number;
  //     definitionIds: string[];
  //     productDefinitionNames: string;
  //     locationDefinitionNames: string;
  //   };
  //   productIds: string[];
  //   userId?: string;
  //   startDate: string;
  //   endDate: string;
  //   productFilters: {
  //     brandIds: string[];
  //     categoryIds: string[];
  //     departmentIds: string[];
  //   };
  // }) {
  //   return this.httpClient
  //     .post<IStockMovementReport>(
  //       `${this.base}api/v${this.version}/${params.entityId}/Reports/Export/StockMovement`,
  //       this.helperService.sanitizeParams(params)
  //     )
  //     .pipe(catchError(() => of(null)));
  // }

  generateStockMovementReport(params: {
    entityId: string;
    locationIds: string[];
    definitionsFilters: {
      targets: number;
      definitionIds: string[];
      productDefinitionNames: string;
      locationDefinitionNames: string;
    };
    productIds: string[];
    userId?: string;
    startDate: string;
    endDate: string;
    productFilters: {
      brandIds: string[];
      categoryIds: string[];
      departmentIds: string[];
    };
  }): Observable<any> {
    return this.httpClient.post<Blob>(
      `${this.base}api/v${this.version}/${params.entityId}/Reports/Export/StockMovement`,
      this.helperService.sanitizeParams(params),
      {
        responseType: 'blob' as 'json', // Set the response type to blob
        observe: 'response', // Set the observe type to response
      }
    );
  }

  generateStockTakeSummary(params: {
    entityId: string;
    locationIds?: string[];
    stockTakeIds?: string[];
    startDate?: string;
    endDate?: string;
  }): Observable<any> {
    const { locationIds, stockTakeIds, startDate, endDate } = params;

    return this.httpClient.post<Blob>(
      `${this.base}api/v${this.version}/${params.entityId}/Reports/Export/StockTake`,
      this.helperService.sanitizeParams(params),
      {
        responseType: 'blob' as 'json',
        observe: 'response',
      }
    );
  }

  loadDeliveryReport(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: {
      targets: number;
      definitionIds: string[];
      productDefinitionNames: string;
      locationDefinitionNames: string;
      customerDefinitionNames: string;
    },
    productIds: string[] = [],
    customerEntityIds: string[] = [],
    userId: string = '',
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    userIds: string[] = []
  ): Observable<any> {
    // TODO REPLACE ANY
    const params = {
      locationIds: locationIds,
      definitionsFilters,
      productIds: productIds,
      customerEntityIds,
      entityId,
      productFilters: {
        brandIds: selectedBrands,
        categoryIds: selectedCategories,
        departmentIds: selectedDepartments,
      },
      userId,
      startDate,
      endDate,
      userIds: userIds,
    };

    return this.httpClient.post<IReportDelivery>(
      `${this.base}api/v${this.version}/${entityId}/Reports/Delivery`,
      this.helperService.sanitizeParams(params)
    );
  }

  loadTransferReport(
    entityId: string,
    params: {
      supplyingLocationIds: string[];
      receivingLocationIds: string[];
      startDate: string;
      endDate: string;
      dateCriteria: OrderDateCriteria;
      userCriteria: OrderUserCriteria[];
    }
  ) {
    return this.httpClient.post<IReportTransfers>(
      `${this.base}api/v${this.version}/${entityId}/Reports/Transfer`,
      this.helperService.sanitizeParams(params)
    );
  }

  exportTransferReport(
    entityId: string,
    params: {
      supplyingLocationIds: string[];
      receivingLocationIds: string[];
      startDate: string;
      endDate: string;
      dateCriteria: OrderDateCriteria;
      userCriteria: OrderUserCriteria[];
    }
  ) {
    //TODO Include any logic we want to check before wasting the API's time

    return this.httpClient.post<Blob>(
      `${this.base}api/v${this.version}/${entityId}/Reports/Export/Transfer`,
      this.helperService.sanitizeParams(params),
      {
        responseType: 'blob' as 'json', // Set the response type to blob
        observe: 'response', // Set the observe type to response
      }
    );
  }

  loadTaxReport(
    entityId: string,
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    customerIds: string[] = [],
    userId: string = '',
    startDate: string = '',
    endDate: string = '',
    selectedCategories: string[] = [],
    selectedDepartments: string[] = [],
    selectedBrands: string[] = [],
    handshake: boolean | null = null,
    deliveredByUserId: string = ''
  ): Observable<ITaxReport> {
    const params = {
      locationIds,
      definitionsFilters,
      productIds,
      productFilters: {
        brandIds: selectedBrands,
        categoryIds: selectedCategories,
        departmentIds: selectedDepartments,
      },
      customerEntityIds: customerIds,
      entityId,
      startDate,
      endDate,
      handshake,
      deliveredByUserId,
      userId,
    };

    return this.httpClient.post<ITaxReport>(
      `${this.base}api/v${this.version}/${entityId}/Reports/TaxReport`,
      this.helperService.sanitizeParams(params)
    );
  }
}
